// Shared presentational building blocks used across the pages. Kept intentionally
// small and dependency-free; all styling comes from the global stylesheet via
// class names and the --plm-* CSS variables.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { NodeInfo, Tier } from '@/engine/types';
import { copyText } from '@/lib/url';

const TIER_LABEL: Record<Tier, string> = {
  lan: 'LAN',
  isp: 'ISP',
  region: 'Region',
  world: 'World',
};

/** A titled card with an optional action slot in its header. */
export function Panel({
  title,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`panel${className ? ` ${className}` : ''}`}>
      {(title || actions) && (
        <header className="panel__head">
          {title ? <h2 className="panel__title">{title}</h2> : <span />}
          {actions ? <div className="panel__actions">{actions}</div> : null}
        </header>
      )}
      <div className="panel__body">{children}</div>
    </section>
  );
}

/** A labelled form field with an optional hint. */
export function Field({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {hint ? <p className="field__hint">{hint}</p> : null}
    </div>
  );
}

/** A constrained numeric input bound to a number value. */
export function NumberInput({
  id,
  value,
  onChange,
  min,
  max,
  step = 1,
  disabled,
}: {
  id?: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
}) {
  return (
    <input
      id={id}
      className="input"
      type="number"
      value={Number.isFinite(value) ? value : ''}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      onChange={(e) => {
        const n = Number(e.target.value);
        if (Number.isFinite(n)) onChange(n);
      }}
    />
  );
}

/** A plain text input. */
export function TextInput({
  id,
  value,
  onChange,
  placeholder,
  disabled,
}: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <input
      id={id}
      className="input"
      type="text"
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** A small pill showing a node's tier. */
export function TierBadge({ tier }: { tier: Tier }) {
  return (
    <span className={`badge badge--${tier}`} title={TIER_LABEL[tier]}>
      {TIER_LABEL[tier]}
    </span>
  );
}

/** A single metric tile with a big value and optional sub-line. */
export function MetricTile({
  label,
  value,
  sub,
  tone = 'neutral',
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'neutral' | 'ok' | 'warn' | 'bad';
}) {
  return (
    <div className={`tile tile--${tone}`}>
      <div className="tile__label">{label}</div>
      <div className="tile__value">{value}</div>
      {sub ? <div className="tile__sub">{sub}</div> : null}
    </div>
  );
}

/** A segmented control (radio group styled as buttons). */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange: (v: T) => void;
  ariaLabel?: string;
}) {
  return (
    <div className="segmented" role="tablist" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          className={`segmented__btn${o.value === value ? ' is-active' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** A button that copies text to the clipboard and briefly confirms. */
export function CopyButton({
  text,
  label,
  className,
}: {
  text: string;
  label?: ReactNode;
  className?: string;
}) {
  const { t } = useTranslation();
  const [done, setDone] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return (
    <button
      type="button"
      className={`btn btn--ghost${className ? ` ${className}` : ''}`}
      onClick={async () => {
        const ok = await copyText(text);
        if (ok) {
          setDone(true);
          window.clearTimeout(timer.current);
          timer.current = window.setTimeout(() => setDone(false), 1600);
        }
      }}
    >
      {done ? t('common.copied') : (label ?? t('common.copyLink'))}
    </button>
  );
}

/** A picker listing nodes with tier badges, health and load. */
export function NodePicker({
  nodes,
  selected,
  onToggle,
  disabled,
}: {
  nodes: NodeInfo[];
  selected: string[];
  onToggle: (id: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  if (nodes.length === 0) {
    return <p className="muted">{t('nodes.empty')}</p>;
  }
  return (
    <ul className="nodelist" role="group" aria-label={t('nodes.pick')}>
      {nodes.map((n) => {
        const isSel = selected.includes(n.id);
        const place = [n.city, n.country].filter(Boolean).join(', ');
        return (
          <li key={n.id} className={`nodelist__item${isSel ? ' is-selected' : ''}`}>
            <label className="nodelist__label">
              <input
                type="checkbox"
                checked={isSel}
                disabled={disabled || !n.healthy}
                onChange={() => onToggle(n.id)}
              />
              <TierBadge tier={n.tier} />
              <span className="nodelist__name">{n.name}</span>
              {place ? <span className="nodelist__place">{place}</span> : null}
              <span className="nodelist__meta">
                {!n.healthy ? (
                  <span className="badge badge--down">{t('nodes.unhealthy')}</span>
                ) : n.load !== undefined ? (
                  <span className="muted">
                    {t('nodes.load')} {(n.load * 100).toFixed(0)}%
                  </span>
                ) : null}
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}

/** A thin progress bar (0..1). */
export function ProgressBar({ value, label }: { value: number; label?: string }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div
      className="progress"
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div className="progress__fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

/** A muted empty-state note. */
export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}
